import React from 'react'
import {
  Container,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'

import { Link as RouterLink } from 'react-router-dom'

import {
  lang,
  url,
} from '@common-sense-privacy/common'

import useRouteParams from '@/hooks/useRouteParams'
import { useCurrentOrganization } from '@/hooks/useSession'

import {
  useGetEvaluationQuery,
  useGetLatestEvaluationQuery,
} from '@/services/organization/product/evaluation'

import CircularLoading from '@/components/CircularLoading'

import QuestionCard from './QuestionCard'

export const POLICY_EVALUATION_TITLE = 'Automated Policy Analysis'

export const POLICY_EVALUATION_INTRO = 'We ran an automated evaluation of the privacy policy you gave us at signup, and proposed answers based on the findings.'

export const POLICY_EVALUATION_CONTROL_NOTE = 'These are suggestions only. You can either confirm the answer, change it, or answer "I don\'t know".'

export const POLICY_EVALUATION_EMPTY_MESSAGE = 'The analysis did not find anything it could suggest an answer for. Carry on through the wizard as normal.'

export const BACK_LABEL = 'Back to the wizard'

export const wizardPath = (productId?: string): string => `/wizard/products/${productId}`

/**
 * The evaluation to show is always the product's LATEST one - the same row the wizard
 * card polls - so the screen resolves its id from that endpoint rather than taking one
 * from the URL. There is deliberately no way to address an older evaluation.
 */
function PolicyEvaluation(): React.ReactElement {
  const { id: organizationId } = useCurrentOrganization()
  const { productId } = useRouteParams()

  const {
    data: latest,
    isError: isLatestError,
    isLoading: isLoadingLatest,
  } = useGetLatestEvaluationQuery({
    params: {
      id: organizationId,
      productId: `${productId}`,
    },
  })

  const productEvaluationId = latest?.data?.id

  const {
    data: results,
    error: resultsError,
    isError: isResultsError,
    isLoading: isLoadingResults,
  } = useGetEvaluationQuery(
    {
      params: {
        id: organizationId,
        productEvaluationId: `${productEvaluationId}`,
        productId: `${productId}`,
      },
    },
    { skip: !productEvaluationId },
  )

  const answers = results?.data?.answers

  const backLink = (
    <Link
      component={RouterLink}
      to={wizardPath(productId)}
      variant='body2'
      sx={{
        alignItems: 'center',
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'row',
        fontWeight: 'bold',
        textDecoration: 'none',
      }}
    ><KeyboardArrowLeftIcon /> {BACK_LABEL}
    </Link>
  )

  // The results query is skipped until the latest endpoint has answered, so its own
  // `isLoading` never covers that first hop - both have to be waited on.
  if (isLoadingLatest || (!!productEvaluationId && isLoadingResults)) {
    return <CircularLoading />
  }

  // A product that has never been evaluated has no results to show either, so it lands
  // in the same state as a failed request rather than a third, emptier one.
  const hasFailed = isLatestError || isResultsError || !productEvaluationId

  if (hasFailed) {
    const message = (resultsError as { data?: { message?: string } })?.data?.message
      || lang().messages.somethingWentWrong()

    return (
      <Container>
        {backLink}
        <Stack mt={4} data-testid='policy-evaluation-error'>
          <Typography variant='h1' mb={4}>{POLICY_EVALUATION_TITLE}</Typography>
          <Typography role='alert'>{message}</Typography>
        </Stack>
      </Container>
    )
  }

  return (
    <Container>
      {backLink}
      <Stack mt={4}>
        <Typography variant='h1' mb={4}>{POLICY_EVALUATION_TITLE}</Typography>
        <Typography mb={2}>{POLICY_EVALUATION_INTRO}</Typography>
        <Typography mb={2}>{POLICY_EVALUATION_CONTROL_NOTE}</Typography>
        {!!results?.data?.policyUrl && (
          <Typography data-testid='policy-evaluation-policy-url'>
            Policy analyzed: {' '}
            <Link href={results.data.policyUrl} target='_blank' rel='noreferrer'>{results.data.policyUrl}</Link>
          </Typography>
        )}
      </Stack>
      {!answers?.length ? (
        <Typography mt={4} data-testid='policy-evaluation-empty'>
          {POLICY_EVALUATION_EMPTY_MESSAGE}
        </Typography>
      ) : (
        // One card per answer, in the order the API returned them - the backend already
        // sorts numerically by code, and a client-side sort would be a string comparison
        // that puts 10.1.1 before 2.1.1.
        <Stack spacing={3} mt={4} data-testid='policy-evaluation-answers'>
          {answers.map(answer => <QuestionCard answer={answer} key={answer.code} />)}
          <Typography>Done confirming your answers? Go {' '}
            <Link href={url.webApp('/wizard')}> on to the wizard</Link>
            {' '} and answer the rest of our privacy questions.
          </Typography>
        </Stack>
      )}
    </Container>
  )
}

export default React.memo(PolicyEvaluation)
